import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import { userRoles } from 'src/types/roleTypes';
import AppSkeleton from 'src/components/AppSkeleton';
const NewCoPilotSpotlight = React.lazy(
  () => import('src/components/CoPilotSpotlight/NewCoPilotSpotlight'),
);

const RedirectComponent = React.lazy(() =>
  import('src/components/Redirect').then((module) => ({ default: module.default })),
);
const RedirectWithSingleParams = React.lazy(() =>
  import('src/components/Redirect').then((module) => ({
    default: module.RedirectWithSingleParams,
  })),
);
const ProtectedRoutes = React.lazy(() => import('../app/hoc/privateRoute'));
const PublicRoutes = React.lazy(() => import('../app/hoc/publicRoutes'));
const Dashboard = React.lazy(() => import('../features/Dashboard'));
const SourceContactsPage = React.lazy(() => import('../features/SourceContactsPage'));
const AccountsRoute = React.lazy(() =>
  import('../features/Dashboard/accountsRoute').then(({ AccountsRoute }) => ({
    default: AccountsRoute,
  })),
);
const ContactsRoute = React.lazy(() =>
  import('../features/Dashboard/contactRoute').then(({ ContactsRoute }) => ({
    default: ContactsRoute,
  })),
);
const AccountConfig = React.lazy(
  () => import('src/features/adminDashboard/components/AccountConfig'),
);
const FeatureComponent = React.lazy(
  () => import('src/features/adminDashboard/components/AccountConfig/components/Features'),
);
const SsoConfigComponent = React.lazy(
  () => import('src/features/adminDashboard/components/AccountConfig/components/SsoConfig'),
);
const ApiKeysSettings = React.lazy(() => import('src/features/settings/modules/apiKeys'));
const ApiConfigurationComponent = React.lazy(
  () => import('src/features/adminDashboard/components/AccountConfig/components/ApiConfiguration'),
);
const GlobalCreditConsumption = React.lazy(
  () => import('src/features/adminDashboard/components/GlobalCreditConsumption'),
);

const PlaybookTemplates = React.lazy(
  () => import('src/features/adminDashboard/components/PlaybookTemplates'),
);

const PartnersManagement = React.lazy(
  () => import('src/features/adminDashboard/components/PartnersManagement'),
);
const CreatePartner = React.lazy(
  () => import('src/features/adminDashboard/components/PartnersManagement/CreatePartner'),
);
const AssignTenants = React.lazy(
  () => import('src/features/adminDashboard/components/PartnersManagement/AssignTenants'),
);

const UsersComponent = React.lazy(
  () => import('src/features/adminDashboard/components/AccountConfig/components/Users'),
);
const CreditManagement = React.lazy(
  () => import('src/features/adminDashboard/components/AccountConfig/components/Credit'),
);
const AccountDetails = React.lazy(
  () => import('src/features/adminDashboard/components/AccountConfig/components/AccountDetails'),
);
const ForgotPassword = React.lazy(() => import('src/features/forgotPassword'));
const ChangePassword = React.lazy(() =>
  import('src/features/changePassword').then(({ ChangePassword }) => ({ default: ChangePassword })),
);
const Login = React.lazy(() =>
  import('src/features/Login/Login').then(({ Login }) => ({ default: Login })),
);
const ResetPasswordSuccess = React.lazy(() =>
  import('src/features/resetPasswordSuccess').then(({ ResetPasswordSuccess }) => ({
    default: ResetPasswordSuccess,
  })),
);
const AdminDashboard = React.lazy(() =>
  import('src/features/adminDashboard').then(({ AdminDashboard }) => ({ default: AdminDashboard })),
);
const ForgotPasswordSuccess = React.lazy(() =>
  import('src/features/forgotPasswordSuccess').then(({ ForgotPasswordSuccess }) => ({
    default: ForgotPasswordSuccess,
  })),
);
const ResendInvitation = React.lazy(() => import('src/features/ResendInvitation'));
const AccountManagement = React.lazy(() => import('src/components/AccountManagement'));
const Settings = React.lazy(() => import('src/features/settings'));
const WebsiteAccountList = React.lazy(() => import('src/features/websiteAccountList'));
const DashboardProvider = React.lazy(() =>
  import('src/hooks/useDashboard').then(({ DashboardProvider }) => ({
    default: DashboardProvider,
  })),
);
const WebsiteAccountProvider = React.lazy(() =>
  import('src/hooks/useWebsiteAccount').then(({ WebsiteAccountProvider }) => ({
    default: WebsiteAccountProvider,
  })),
);
const ChampionsTrackingProvider = React.lazy(() =>
  import('src/hooks/useChampionsTracking').then(({ ChampionsTrackingProvider }) => ({
    default: ChampionsTrackingProvider,
  })),
);
const Accounts = React.lazy(
  () => import('src/features/settings/modules/icpFiltersConfiguration/Accounts'),
);
const Users = React.lazy(() => import('src/features/settings/components/setting-item/Users'));
const RolesAndPermissions = React.lazy(() => import('src/features/RolesAndPermissions'));
const CreateRole = React.lazy(() => import('src/features/RolesAndPermissions/CreateRole'));
const UpdateRole = React.lazy(() => import('src/features/RolesAndPermissions/UpdateRole'));
const CloneRole = React.lazy(() => import('src/features/RolesAndPermissions/CloneRole'));
const ExclusionList = React.lazy(
  () => import('src/features/settings/modules/icpFiltersConfiguration/exclusionList'),
);
const WebsiteTracking = React.lazy(
  () => import('src/features/settings/modules/icpFiltersConfiguration/WebsiteTrackingV2'),
);
const MultiDBExternal = React.lazy(() => import('src/features/multiDbExternal'));
const SettingsProvider = React.lazy(() =>
  import('src/hooks/useSettings').then(({ SettingsProvider }) => ({ default: SettingsProvider })),
);
const ViewListContentV2 = React.lazy(() => import('src/features/Dashboard/listRoute'));
const ListDetails = React.lazy(() => import('src/features/Dashboard/components/ListDetails'));
const CreateIcp = React.lazy(() => import('src/features/Icp/CreateIcp'));
const UpdateIcp = React.lazy(() => import('src/features/Icp/UpdateIcp'));
const CloneIcp = React.lazy(() => import('src/features/Icp/CloneIcp'));
const Icp = React.lazy(() => import('src/features/Icp'));
const CustomFields = React.lazy(() =>
  import('src/features/settings/modules/icpFiltersConfiguration/CustomFields').then(
    ({ CustomFields }) => ({ default: CustomFields }),
  ),
);
const WebsiteAgent = React.lazy(
  () => import('src/features/settings/agentSetup/components/websiteAgent'),
);
const BasicSetup = React.lazy(
  () => import('src/features/settings/agentSetup/components/websiteAgent/BasicSetup'),
);
const General = React.lazy(
  () => import('src/features/settings/agentSetup/components/websiteAgent/General'),
);
const SproutsSignals = React.lazy(
  () => import('src/features/settings/agentSetup/components/websiteAgent/SproutsSignals'),
);
const KnowledgeBase = React.lazy(
  () => import('src/features/settings/agentSetup/components/websiteAgent/KnowledgeBase'),
);
const ConversationSetup = React.lazy(
  () => import('src/features/settings/agentSetup/components/websiteAgent/ConversationSetup'),
);
const RaspberryDashboard = React.lazy(() => import('src/features/Raspberry-dashboard'));
// import ListHpm from 'src/features/Dashboard/components/ListHpm';
// import SyncStatusV3 from 'src/features/settings/integrations/pages/sync-status-v3';
import { userEmail, userRole } from 'src/store/login';
import { SPROUTS_ADMIN_DOMAIN } from './constants';
import { getDomainFromEmail, hasSettingsPermission } from 'src/utils/utilityMethods';
const AIMessageScreen = React.lazy(() => import('src/features/AIMessageScreen'));
// import ListHpmV3 from 'src/features/Dashboard/components/ListHpmV3';
const ChatbotSiri = React.lazy(() => import('src/components/ChatbotSiri'));
const SignUp = React.lazy(() => import('src/features/Signup'));
const ActivateUser = React.lazy(() => import('src/features/ActivateUser'));
const VerifyMagicLink = React.lazy(() => import('src/features/VerifyMagicLink'));
const DatabaseSearchProvider = React.lazy(() =>
  import('src/hooks/useDatabase').then(({ DatabaseSearchProvider }) => ({
    default: DatabaseSearchProvider,
  })),
);

const PermissionDenied = React.lazy(() =>
  import('src/features/RolesAndPermissions/PermissionDenied'),
);
const EnrichmentLogsProvider = React.lazy(() =>
  import('src/hooks/useEnrichmentLogs').then(({ EnrichmentLogsProvider }) => ({
    default: EnrichmentLogsProvider,
  })),
);
const IcpListsV3 = React.lazy(() =>
  import('src/features/settings/modules/icpFiltersConfiguration/IcpListsV3'),
);
const OAuthCompleted = React.lazy(() => import('src/components/OAuthCompleted'));
const RechargeHistory = React.lazy(
  () => import('src/features/settings/credits/RechargeHistory'),
);
const CreditUsage = React.lazy(
  () => import('src/features/settings/credits/CreditUsage'),
);
const AssignCredits = React.lazy(
  () => import('src/features/settings/credits/AssignCredits'),
);
const Template = React.lazy(() => import('src/features/workflows/templates'));
// const WorkflowEditor = React.lazy(() => import('src/features/workflows/workflowEditor'));
const Playbooks = React.lazy(() => import('src/features/workflows/playbooks'));
// import RaspberryDashboard from 'src/features/Raspberry-dashboard';
// import ListHpm from 'src/features/Dashboard/components/ListHpm';
const SourceContactsProvider = React.lazy(() =>
  import('src/hooks/useSourceContacts').then(({ SourceContactsProvider }) => ({
    default: SourceContactsProvider,
  })),
);
const Onboarding = React.lazy(() => import('src/features/Onboarding'));
const OnboardingTour = React.lazy(() => import('src/features/Onboarding/modules/OnboardingTour'));
const SetupICP = React.lazy(() => import('src/features/Onboarding/modules/setupICP'));
const SetupIntentTopics = React.lazy(
  () => import('src/features/Onboarding/modules/setupIntentTopics'),
);
const SetupWebsiteReveal = React.lazy(
  () => import('src/features/Onboarding/modules/setupWebsiteReveal'),
);
const InviteUsers = React.lazy(() => import('src/features/Onboarding/modules/inviteUsers'));
const IntegrationsView = React.lazy(
  () => import('src/features/Onboarding/modules/integrationsView'),
);
const BulkImportV2 = React.lazy(() => import('src/features/Dashboard/components/ImportLogs'));
const FileSelection = React.lazy(() => import('src/components/ImportV2/components/FileSelection'));
const ColumnMapping = React.lazy(() => import('src/components/ImportV2/components/ColumnMapping'));

const AssignAccounts = React.lazy(
  () => import('src/features/Dashboard/components/MultiChildAccounts/components/Accounts'),
);
const AssignContacts = React.lazy(
  () => import('src/features/Dashboard/components/MultiChildAccounts/components/Contacts'),
);
const ImportPreferences = React.lazy(
  () => import('src/components/ImportV2/components/ImportPreferences'),
);


const WorkflowEditor = React.lazy(() => import('src/features/PlaybookWorkflow'));
const Playbook = React.lazy(() => import('src/features/workflows/playbooks'));
const ApprovalsPage = React.lazy(() => import('src/features/Approvals'));
const ApprovalDetailPage = React.lazy(() => import('src/features/Approvals/ApprovalDetail'));
const CampaignDashboard = React.lazy(() => import('src/features/campaignDashboard'));
const ExclusionImport = React.lazy(() =>
  import(
    'src/features/settings/modules/icpFiltersConfiguration/exclusionList/ExclusionListImport'
  ).then(({ ExclusionImport }) => ({ default: ExclusionImport })),
);
const PageNotFound = React.lazy(() => import('src/components/PageNotFound'));
const BulkImport = React.lazy(() =>
  import('src/features/Dashboard/components/BulkImport').then(({ BulkImport }) => ({
    default: BulkImport,
  })),
);
const IntegrationsList = React.lazy(
  () => import('src/features/settings/integrations/pages/integrations-list'),
);
const Integrate = React.lazy(() => import('src/features/settings/integrations/pages/integrate'));
const AuthOnlyCallback = React.lazy(
  () => import('src/features/settings/integrations/pages/auth-callback'),
);
const Manage = React.lazy(() => import('src/features/settings/integrations/pages/manage'));
const MappingsUpdateLayout = React.lazy(
  () => import('src/features/settings/integrations/components/mappings-update-layout'),
);
const UserMappingsV2 = React.lazy(
  () => import('src/features/settings/integrations/components/user-mappings-v2'),
);
const FieldMappingsV2 = React.lazy(
  () => import('src/features/settings/integrations/components/field-mappings-v2'),
);
const EditMappings = React.lazy(
  () => import('src/features/settings/integrations/pages/edit-mappings'),
);
// const SyncStatusV2 = React.lazy(
//   () => import('src/features/settings/integrations/pages/sync-status-v2'),
// );
const SyncStatusV3 = React.lazy(
  () => import('src/features/settings/integrations/pages/sync-status-v3'),
);
const ConfigureIntegration = React.lazy(
  () => import('src/features/settings/integrations/pages/configure-integration'),
);
const ChromeExtensionRedirect = React.lazy(() => import('src/features/chromeExtensionRedirect'));
const LinkedInConnectRedirect = React.lazy(() => import('src/features/linkedInConnectRedirect'));
const SyncScenario = React.lazy(
  () => import('src/features/settings/integrations/components/sync-scenario'),
);
// const WebsiteVisitors = React.lazy(() => import('src/features/websiteAccountList/WebsiteVisitors'));
const WebsiteAccounts = React.lazy(
  () => import('src/features/websiteAccountList/WebsiteVisitors/AccountSection'),
);
const WebsiteContacts = React.lazy(
  () => import('src/features/websiteAccountList/WebsiteVisitors/ContactSection'),
);

const TestRunV2 = React.lazy(
  () => import('src/features/settings/integrations/components/test-run-v2'),
);

const IntentTopicsPageV2 = React.lazy(
  () => import('src/features/settings/modules/icpFiltersConfiguration/IntentTopicsV2'),
);

const PlaybookLogs = React.lazy(() => import('src/components/PlaybookLogs'));
const MillerHiemenRoles = React.lazy(() => import('src/features/MillerHiemenRoles'));
const HPMConfiguration = React.lazy(() => import('src/features/HPMConfiguration'));
// const Playbook = React.lazy(
//   () => import('src/features/settings/modules/icpFiltersConfiguration/Playbook'),
// );
// const PlaybookList = React.lazy(
//   () => import('src/features/settings/modules/icpFiltersConfiguration/Playbook/PlaybookList'),
// );
// const CreatePlaybook = React.lazy(
//   () => import('src/features/settings/modules/icpFiltersConfiguration/Playbook/CreatePlaybook'),
// );
// const EditPlaybook = React.lazy(
//   () => import('src/features/settings/modules/icpFiltersConfiguration/Playbook/EditPlaybook'),
// );

// const PlaybookV2 = React.lazy(
//   () => import('src/features/settings/modules/icpFiltersConfiguration/PlaybookV2'),
// );
const PlaybookListV2 = React.lazy(
  () => import('src/features/settings/modules/icpFiltersConfiguration/PlaybookV2/PlaybookListV2'),
);
const CreatePlaybookV2 = React.lazy(
  () => import('src/features/settings/modules/icpFiltersConfiguration/PlaybookV2/CreatePlaybook'),
);
const ArchivedPlaybookList = React.lazy(
  () =>
    import('src/features/settings/modules/icpFiltersConfiguration/PlaybookV2/ArchivedPlaybookList'),
);

// const CreateTemplateTalAccountReveal = React.lazy(
//   () =>
//     import(
//       'src/features/settings/modules/icpFiltersConfiguration/PlaybookV2/template-tal-contact-reveal'
//     ),
// );

// const CreateTemplateAccountReveal = React.lazy(
//   () =>
//     import(
//       'src/features/settings/modules/icpFiltersConfiguration/PlaybookV2/template-account-reveal'
//     ),
// );

const CreateTemplateDynamic = React.lazy(
  () => import('src/features/settings/modules/icpFiltersConfiguration/PlaybookV2/template-dynamic'),
);

// const CreateTemplateLeadAddedToTal = React.lazy(
//   () =>
//     import(
//       'src/features/settings/modules/icpFiltersConfiguration/PlaybookV2/template-lead-added-to-tal'
//     ),
// );

// const CreateTemplateContactReveal = React.lazy(
//   () =>
//     import(
//       'src/features/settings/modules/icpFiltersConfiguration/PlaybookV2/template-contact-reveal'
//     ),
// );

// const EditTemplateLeadAddedToTal = React.lazy(
//   () =>
//     import(
//       'src/features/settings/modules/icpFiltersConfiguration/PlaybookV2/template-lead-added-to-tal'
//     ),
// );

// const EditTemplateTalAccountReveal = React.lazy(
//   () =>
//     import(
//       'src/features/settings/modules/icpFiltersConfiguration/PlaybookV2/template-tal-contact-reveal'
//     ),
// );

const EditTemplateDynamic = React.lazy(
  () => import('src/features/settings/modules/icpFiltersConfiguration/PlaybookV2/template-dynamic'),
);

// const EditTemplateAccountReveal = React.lazy(
//   () =>
//     import(
//       'src/features/settings/modules/icpFiltersConfiguration/PlaybookV2/template-account-reveal'
//     ),
// );

// const EditTemplateContactReveal = React.lazy(
//   () =>
//     import(
//       'src/features/settings/modules/icpFiltersConfiguration/PlaybookV2/EditPlaybook/template-contact-reveal'
//     ),
// );
const CallRecordings = React.lazy(() => import('src/features/callRecordings'));

const CTFileSelection = React.lazy(
  () => import('src/components/ChampionTrackingImport/components/FileSelection'),
);
const CTColumnMapping = React.lazy(
  () => import('src/components/ChampionTrackingImport/components/ColumnMapping'),
);

const ChampionsTracking = React.lazy(
  () => import('src/features/settings/modules/icpFiltersConfiguration/ChampionTracking'),
);

const ChampionsWithJobChange = React.lazy(
  () =>
    import(
      'src/features/Dashboard/components/TalChampionsTracking/components/ChampionsWithJobChange'
    ),
);

const AllChampions = React.lazy(
  () => import('src/features/Dashboard/components/TalChampionsTracking/components/AllChampions'),
);

const ChampionsMovedToTAL = React.lazy(
  () =>
    import(
      'src/features/Dashboard/components/TalChampionsTracking/components/ChampioinsMovedToTAL'
    ),
);
const ZoomAccountAssignment = React.lazy(() => import('src/features/meetings/Index'));
const Opportunities = React.lazy(() => import('src/features/Opportunities'));

const ContactDetailsPage = React.lazy(() => import('src/features/ContactDetailPage'));
const ContactDetailNotes = React.lazy(
  () => import('src/features/ContactDetailPage/components/ContactNotes'),
);
// const ContactDetailActivity = React.lazy(
//   () => import('src/features/ContactDetailPage/components/ContactActivity'),
// );
const ContactOverviewPage = React.lazy(
  () => import('src/features/ContactDetailPage/components/ContactOverviewPage'),
);
const ContactActivity = React.lazy(
  () => import('src/features/ContactDetailPage/components/ContactActivity'),
);

// import withScriptPreload from 'src/hoc/withScriptPreload';
const LoginWithPassword = React.lazy(() =>
  import('src/features/LoginWithPassword').then(({ LoginWithPassword }) => ({
    default: LoginWithPassword,
  })),
);
const MagicLink = React.lazy(() =>
  import('src/features/MagicLink').then(({ MagicLink }) => ({
    default: MagicLink,
  })),
);
const SsoLogin = React.lazy(() => import('src/features/SsoLogin'));
const AccountDetailsProvider = React.lazy(() =>
  import('src/hooks/useAccountDetails').then(({ AccountDetailsProvider }) => ({
    default: AccountDetailsProvider,
  })),
);
const ContactDetailsProvider = React.lazy(() =>
  import('src/hooks/useContactDetails').then(({ ContactDetailsProvider }) => ({
    default: ContactDetailsProvider,
  })),
);

const AccountOverviewPage = React.lazy(
  () => import('src/features/AccountDetailsPage/components/AccountsOverview'),
);

const AccountDetailsPage = React.lazy(() => import('src/features/AccountDetailsPage'));
const DeepIntelligence = React.lazy(() => import('src/features/DeepIntelligence'));
const ContactsPage = React.lazy(
  () => import('src/features/AccountDetailsPage/components/ContactsPage'),
);
const SignalsPage = React.lazy(
  () => import('src/features/AccountDetailsPage/components/SignalsPage'),
);
const ActivityPage = React.lazy(
  () => import('src/features/AccountDetailsPage/components/ActivityPage'),
);
const NotesPage = React.lazy(() => import('src/features/AccountDetailsPage/components/NotesPage'));
const ArchivedRecordsProvider = React.lazy(() =>
  import('src/hooks/useArchivedRecords').then(({ ArchivedRecordsProvider }) => ({
    default: ArchivedRecordsProvider,
  })),
);
const ArchivedAccountsPage = React.lazy(
  () => import('src/features/ArchivedRecordsPage/ArchivedAccountsPage'),
);
const ArchivedContactsPage = React.lazy(
  () => import('src/features/ArchivedRecordsPage/ArchivedContactsPage'),
);

const PlaybookWorkflow = React.lazy(() => import('src/features/PlaybookWorkflow'));

const LogsPage = React.lazy(() => import('src/features/Dashboard/components/TargetProfilesLogs'));
const EnrichmentLogs = React.lazy(() => import('src/features/Dashboard/components/EnrichmentLogs'));
const IntegrationLogs = React.lazy(() => import('src/features/Dashboard/components/IntegrationLogs'));
const CredentialManagementPage = React.lazy(
  () => import('src/features/credentialManagement/CredentialManagementPage'),
);
const TestIntegration = React.lazy(() => import('src/features/TestIntegration'));
const NotificationsConfigs = React.lazy(() => import('src/features/settings/notificationsConfigs'));
const MoveToTalLogs = React.lazy(() => import('src/features/Dashboard/components/MoveToTalLogs'));
const SequenceEnrollment = React.lazy(() => import('src/features/settings/modules/icpFiltersConfiguration/SequenceEnrollment'));
interface IProps {
  element: React.ReactNode;
  permission: boolean;
}

const PermissionRoute = ({ element, permission }: IProps) => {
  if (permission) return element;
  return <PermissionDenied />;
};

const MainRoutes = () => {
  const featureFlags = useFeatureFlags();
  const adminFeatureFlags = useSelector((state: any) => state.adminFeatureFlags);
  const emailID = useSelector(userEmail);
  const role = useSelector(userRole);
  const {
    canBulkImportContactsCSV,
    canSetupIntegrations,
    canSetupIntentTopics,
    canSetupIcp,
    canSetupCustomFields,
    canSetupWebsiteTracking,
    // canSetupAgents,
    canSetupUsers,
    canSetupRolesAndPermissions,
    canAccessWebsiteReveal_module,
    canAccessDatabaseSearch_module,
    canAccessSettings_module,
    canSetupMillerHeimanRoles,
    canSetupHPMConfig,
    canAssignCredits
  } = useSelector((state: any) => state.rolePermissions);

  return (
    <Suspense fallback={<AppSkeleton />}>
      <Routes>
        {/** Protected Routes */}
        {/** Wrap all Route under ProtectedRoutes element */}
        <Route
          path="/"
          element={
            <ProtectedRoutes
              roles={[userRoles.Admin, userRoles.User, userRoles.SuperAdmin, userRoles.DataOps, userRoles.CustomerSupport]}
            />
          }
        >
          {/* <Route path="/extension/login" element={<ChromeExtensionRedirect />} /> */}
          <Route index element={<Navigate replace to="target-profiles/accounts" />} />

          <Route path="hpm/list/:id/:listName" element={<AIMessageScreen />} />
          <Route path="hpm/contacts" element={<AIMessageScreen />} />
          {/* <Route path="target-profiles/hpm/list/:id/:listName" element={<ListHpm />} /> */}
          {/* <Route path="target-profiles/import" element={<BulkImport />} /> */}
          <Route path="target-profiles/import" element={<BulkImport />}>
            <Route index path="" />
            <Route path="mapping" />
            <Route path="result" />
          </Route>
          <Route path="dashboard">
            <Route path="accounts" element={<RedirectComponent to="/target-profiles/accounts" />} />
            <Route path="contacts" element={<RedirectComponent to="/target-profiles/contacts" />} />
            <Route path="list" element={<RedirectComponent to="/target-profiles/list" />} />
          </Route>
          <Route path="run-campaign-rasp/*" element={<RaspberryDashboard />} />
          {featureFlags?.automated_playbook && (
            <>
              <Route path="template">
                {/* <Route
                  index
                  element={
                    <PermissionRoute
                      element={<PlaybookListV2 />}
                      permission={role === 'User' || role === 'Admin' || role === 'Superadmin'}
                    />
                  }
                /> */}
                <Route
                  path="create"
                  element={
                    <PermissionRoute
                      element={<Template />}
                      permission={role === 'Superadmin' || role === 'Admin'}
                    />
                  }
                />
                <Route
                  path="edit/:id"
                  element={
                    <PermissionRoute
                      element={<Template />}
                      permission={role === 'Superadmin' || role === 'Admin'}
                    />
                  }
                />
              </Route>

              {/* <Route path="playbook">
                <Route
                  path="create/:templateId?"
                  element={
                    <PermissionRoute
                      element={<Playbooks />}
                      permission={role === 'Superadmin' || role === 'Admin'}
                    />
                  }
                />
                <Route
                  path="edit/:id"
                  element={
                    <PermissionRoute
                      element={<Playbooks />}
                      permission={role === 'Superadmin' || role === 'Admin'}
                    />
                  }
                />
              </Route> */}
              <Route path="playbook">
                <Route path="create/:templateId?" element={<Playbooks />} />
                <Route path="edit/:id" element={<Playbooks />} />
              </Route>

              <Route
                path="templates"
                element={
                  <PermissionRoute
                    element={<PlaybookListV2 initialTab={1} />}
                    permission={role === 'User' || role === 'Admin' || role === 'Superadmin' || role === userRoles.CustomerSupport}
                  />
                }
              />
              {/* <Route path="playbook">
                <Route
                  index
                  element={
                    <PermissionRoute
                      element={<PlaybookListV2 />}
                      permission={role === 'User' || role === 'Admin' || role === 'Superadmin'}
                    />
                  }
                />
                <Route
                  path="create"
                  element={
                    <PermissionRoute
                      element={<CreatePlaybookV2 />}
                      permission={role === 'Admin' || role === 'Superadmin'}
                    />
                  }
                />
                <Route
                  path="template-dynamic/:templateId"
                  element={
                    <PermissionRoute
                      element={
                        <SourceContactsProvider>
                          <CreateTemplateDynamic />
                        </SourceContactsProvider>
                      }
                      permission={role === 'Admin' || role === 'Superadmin'}
                    />
                  }
                />
                <Route
                  path="edit/:id"
                  element={
                    <PermissionRoute
                      element={<EditTemplateDynamic />}
                      permission={role === 'Admin' || role === 'Superadmin'}
                    />
                  }
                />
                <Route
                  path="archives"
                  element={
                    <PermissionRoute
                      element={<ArchivedPlaybookList />}
                      permission={role === 'User' || role === 'Admin' || role === 'Superadmin'}
                    />
                  }
                />
              </Route> */}
              <Route path="playbook">
                <Route
                  index
                  element={
                    <PermissionRoute
                      element={<PlaybookListV2 />}
                      permission={role === 'User' || role === 'Admin' || role === 'Superadmin' || role === userRoles.CustomerSupport}
                    />
                  }
                />
                <Route path="create" element={<CreatePlaybookV2 />} />
                <Route
                  path="template-dynamic/:templateId"
                  element={
                    <SourceContactsProvider>
                      <CreateTemplateDynamic />
                    </SourceContactsProvider>
                  }
                />
                <Route path="logs" element={<PlaybookLogs />} />
                <Route path="edit/:id" element={<EditTemplateDynamic />} />
                <Route path="archives" element={<ArchivedPlaybookList />} />
              </Route>
            </>
          )}

          <Route path="call-recordings" element={<CallRecordings />} />
          <Route path="meetings/assign-zoom-account" element={<ZoomAccountAssignment />} />
          <Route path="workflows/create" element={<PlaybookWorkflow />} />
          <Route path="workflows/edit/:id" element={<PlaybookWorkflow />} />
          {featureFlags?.showOpportunities && (
            <Route path="opportunities" element={<Opportunities />} />
          )}
          {featureFlags.campaignDashboard && (
            <>
              <Route path="dashboard/campaign" element={<CampaignDashboard />} />
              <Route
                path="campaign-dashboard"
                element={<RedirectComponent to="/dashboard/campaign" />}
              />
            </>
          )}

          <Route
            path="dashboard/import"
            element={<RedirectComponent to="/target-profiles/import" />}
          />
          <Route
            path="target-profiles/archived-accounts"
            element={
              <ArchivedRecordsProvider>
                <ArchivedAccountsPage />
              </ArchivedRecordsProvider>
            }
          />
          <Route
            path="target-profiles/archived-contacts"
            element={
              <ArchivedRecordsProvider>
                <ArchivedContactsPage />
              </ArchivedRecordsProvider>
            }
          />

          <Route
            path="target-profiles/source-contacts"
            element={
              <SourceContactsProvider>
                <SourceContactsPage />
              </SourceContactsProvider>
            }
          />
          <Route
            path="target-profiles"
            element={
              <DashboardProvider>
                <Dashboard />
              </DashboardProvider>
            }
          >
            <Route path="accounts" element={<AccountsRoute ICPs={[]} />} />
            <Route path="contacts" element={<ContactsRoute ICPs={[]} />} />
            <Route path="list/:id/:listName" element={<ListDetails />} />
            <Route path="list" element={<ViewListContentV2 />} />

            <Route path="logs" element={<LogsPage />} />
            <Route
              path="contact-enrichment-logs"
              element={
                <EnrichmentLogsProvider>
                  <EnrichmentLogs />
                </EnrichmentLogsProvider>
              }
            />
            <Route
              path="account-enrichment-logs"
              element={
                <EnrichmentLogsProvider>
                  <EnrichmentLogs />
                </EnrichmentLogsProvider>
              }
            />
            <Route path="integration-logs" element={<IntegrationLogs />} />
            <Route
              path="import-logs"
              element={
                <PermissionRoute element={<BulkImportV2 />} permission={canBulkImportContactsCSV} />
              }
            />
            <Route path="move-to-tal-logs" element={<MoveToTalLogs />} />
            <Route path="accounts/import">
              <Route
                path="upload-file"
                element={
                  <PermissionRoute
                    element={<FileSelection />}
                    permission={canBulkImportContactsCSV}
                  />
                }
              />
              <Route
                path="columns-mapping"
                element={
                  <PermissionRoute
                    element={<ColumnMapping />}
                    permission={canBulkImportContactsCSV}
                  />
                }
              />
              <Route
                path="preferences"
                element={
                  <PermissionRoute
                    element={<ImportPreferences />}
                    permission={canBulkImportContactsCSV}
                  />
                }
              />
            </Route>

            <Route
              path="import-logs"
              element={
                <PermissionRoute element={<BulkImportV2 />} permission={canBulkImportContactsCSV} />
              }
            />
            <Route path="accounts/import">
              <Route
                path="upload-file"
                element={
                  <PermissionRoute
                    element={<FileSelection />}
                    permission={canBulkImportContactsCSV}
                  />
                }
              />
              <Route
                path="columns-mapping"
                element={
                  <PermissionRoute
                    element={<ColumnMapping />}
                    permission={canBulkImportContactsCSV}
                  />
                }
              />
              <Route
                path="preferences"
                element={
                  <PermissionRoute
                    element={<ImportPreferences />}
                    permission={canBulkImportContactsCSV}
                  />
                }
              />
            </Route>
            <Route path="contacts/import">
              <Route
                path="upload-file"
                element={
                  <PermissionRoute
                    element={<FileSelection />}
                    permission={canBulkImportContactsCSV}
                  />
                }
              />
              <Route
                path="columns-mapping"
                element={
                  <PermissionRoute
                    element={<ColumnMapping />}
                    permission={canBulkImportContactsCSV}
                  />
                }
              />
              <Route
                path="preferences"
                element={
                  <PermissionRoute
                    element={<ImportPreferences />}
                    permission={canBulkImportContactsCSV}
                  />
                }
              />
            </Route>
            <Route path="accounts/unassigned" element={<AssignAccounts />} />
            <Route path="accounts/unassigned/:id" element={<AssignContacts />} />
          </Route>

          <Route
            path="account-details/:id"
            element={
              <AccountDetailsProvider>
                <AccountDetailsPage />
              </AccountDetailsProvider>
            }
          >
            <Route index path="overview" element={<AccountOverviewPage />} />
            <Route path="contacts" element={<ContactsPage />} />
            <Route path="signals" element={<SignalsPage />} />
            <Route path="activity" element={<ActivityPage />} />
            <Route path="notes" element={<NotesPage />} />
          </Route>
          {featureFlags?.accountIntelligence && (
            <Route path="deep-intelligence/*" element={<DeepIntelligence />} />
          )}
          <Route
            path="contact-details/:id"
            element={
              <ContactDetailsProvider>
                <ContactDetailsPage />
              </ContactDetailsProvider>
            }
          >
            <Route index path="overview" element={<ContactOverviewPage />} />
            <Route path="notes" element={<ContactDetailNotes />} />
            <Route path="activity" element={<ContactActivity />} />
          </Route>
          <Route path="champions-tracking">
            <Route path="champions" element={<AllChampions />} />
            <Route path="champions-with-job-change" element={<ChampionsWithJobChange />} />
            <Route path="champions-moved-to-TAL" element={<ChampionsMovedToTAL />} />

            <Route path="import">
              <Route
                path="upload-file"
                element={
                  <ChampionsTrackingProvider>
                    <CTFileSelection />
                  </ChampionsTrackingProvider>
                }
              />
              <Route
                path="columns-mapping"
                element={
                  <ChampionsTrackingProvider>
                    <CTColumnMapping />
                  </ChampionsTrackingProvider>
                }
              />
            </Route>
          </Route>
          <Route path="workflows/create" element={<WorkflowEditor />} />
          <Route path="workflows/edit/:id" element={<WorkflowEditor />} />

          <Route path="playbook/approvals" element={<ApprovalsPage />} />
          <Route path="playbook/approvals/:token" element={<ApprovalDetailPage />} />

          <Route path="dashboard">
            <Route path="accounts" element={<RedirectComponent to="/target-profiles/accounts" />} />
            <Route path="contacts" element={<RedirectComponent to="/target-profiles/contacts" />} />
            <Route path="list" element={<RedirectComponent to="/target-profiles/list" />} />
          </Route>
          {/* <Route path="run-campaign/*" element={<UpscaleDashboard />} />
          {featureFlags.campaignDashboard && (
            <>
              <Route path="dashboard/campaign" element={<CampaignDashboard />} />
              <Route
                path="campaign-dashboard"
                element={<RedirectComponent to="/dashboard/campaign" />}
              />
            </>
          )} */}

          <Route
            path="website"
            element={
              <WebsiteAccountProvider>
                <PermissionRoute
                  element={<WebsiteAccountList />}
                  permission={canAccessWebsiteReveal_module}
                />
              </WebsiteAccountProvider>
            }
          >
            {/* due to preloading overview dashboard div added in parent component */}
            <Route path="overview" />
            <Route path="visitors">
              <Route index element={<Navigate to="accounts" replace />} />
              <Route path="accounts" element={<WebsiteAccounts />} />
              <Route path="contacts" element={<WebsiteContacts />} />
            </Route>
            <Route path="visitors">
              <Route index element={<Navigate to="accounts" />} />
              <Route
                path="accounts"
                element={<RedirectComponent to="/website/visitors/accounts" />}
              />
              <Route
                path="contacts"
                element={<RedirectComponent to="/website/visitors/contacts" />}
              />
            </Route>
            <Route path="webpages" />
          </Route>
          <Route path="website-accounts" element={<RedirectComponent to="/website/visitors" />} />
          <Route
            path="database-search/"
            element={<RedirectComponent to="/database-search/accounts" />}
          />
          <Route
            path="database-search"
            element={
              <DatabaseSearchProvider>
                <PermissionRoute
                  element={<MultiDBExternal />}
                  permission={canAccessDatabaseSearch_module}
                />
                {/* <CoPilot /> */}
                <NewCoPilotSpotlight />
              </DatabaseSearchProvider>
            }
          >
            <Route path="accounts" />
            <Route path="contacts" />
          </Route>
          <Route path="onboarding/" element={<Onboarding />} />
          <Route path="onboarding/tour" element={<OnboardingTour />} />
          <Route path="onboarding/setup-icp" element={<SetupICP />} />
          <Route path="onboarding/setup-intent-topics" element={<SetupIntentTopics />} />
          <Route path="onboarding/setup-website-reveal" element={<SetupWebsiteReveal />} />
          <Route path="onboarding/setup-crm" element={<IntegrationsView />} />
          <Route path="onboarding/invite-users" element={<InviteUsers />} />

          <Route path="account" element={<AccountManagement />} />
        </Route>
        <Route path="/" element={<ProtectedRoutes roles={[userRoles.SuperAdmin, userRoles.CustomerSupport]} />}>
          <Route path="admin-dashboard/account-activation" element={<AdminDashboard />} />
          <Route
            path="admin-dashboard"
            element={<RedirectComponent to="/admin-dashboard/account-activation" />}
          />

          <Route path="admin-dashboard/resend-invitation" element={<ResendInvitation />} />
          <Route
            path="resend-invitation"
            element={<RedirectComponent to="/admin-dashboard/resend-invitation" />}
          />
          <Route path="admin-dashboard/credit-usage" element={<GlobalCreditConsumption />} />
          <Route path="admin-dashboard/playbook-templates" element={<PlaybookTemplates />} />
          <Route path="admin-dashboard/partners" element={<PartnersManagement />} />
          <Route path="admin-dashboard/partners/create" element={<CreatePartner />} />
          <Route
            path="admin-dashboard/partners/:partnerId/assign-tenants"
            element={<AssignTenants />}
          />
          <Route path="admin-dashboard/hpmconfigurations">
            <Route
              path="insights"
              element={
                <RedirectComponent to="admin-dashboard/account-config/:id/hpmconfigurations/insights" />
              }
            />
            <Route
              path="painpoint"
              element={
                <RedirectComponent to="admin-dashboard/account-config/:id/hpmconfigurations/painpoint" />
              }
            />
          </Route>
          <Route path="admin-dashboard/account-config" element={<AccountConfig />}>
            <Route index element={<Navigate to="account-details" />} />
            <Route path="account-details" element={<AccountDetails />} />
            <Route path="users" element={<UsersComponent />} />
            <Route path="features" element={<FeatureComponent />} />
            <Route path="credit" element={<CreditManagement />} />
            <Route path="sso" element={<SsoConfigComponent />} />
            <Route path="api-configuration" element={<ApiConfigurationComponent />} />
            {/* <Route path="signals" element={<SignalComponent />} /> */}
          </Route>
          <Route path="account-config">
            <Route index element={<Navigate to="account-details" />} />
            <Route
              path="account-details"
              element={<RedirectComponent to="/admin-dashboard/account-config/account-details" />}
            />
            <Route
              path="users"
              element={<RedirectComponent to="/admin-dashboard/account-config/users" />}
            />
            <Route
              path="features"
              element={<RedirectComponent to="/admin-dashboard/account-config/features" />}
            />
            {/* <Route
              path="signals"
              element={<RedirectComponent to="/admin-dashboard/account-config/signals" />}
            /> */}
            <Route
              path="hpmconfig"
              element={<RedirectComponent to="/admin-dashboard/account-config/hpmconfig" />}
            />
          </Route>
        </Route>

        <Route
          path="/"
          element={
            <ProtectedRoutes
              roles={[userRoles.Admin, userRoles.SuperAdmin]}
              permission={canAccessSettings_module}
            />
          }
        >
          {/* <Route path="exclusion-list/import" element={<ExclusionImport />} /> */}
          <Route path="settings/config/exclusion-list/import" element={<ExclusionImport />}>
            <Route index path="" />
            <Route path="mapping" />
            <Route path="result" />
          </Route>

          <Route
            path="exclusion-list/import"
            element={<RedirectComponent to="/settings/config/exclusion-list/import" />}
          />
          <Route path="/settings" element={<Navigate replace to="/settings/account" />} />
          <Route
            path="settings"
            element={
              <SettingsProvider>
                <PermissionRoute element={<Settings />} permission={canAccessSettings_module} />
              </SettingsProvider>
            }
          >
            <Route
              path="integrations"
              element={
                <PermissionRoute
                  element={<IntegrationsList />}
                  permission={hasSettingsPermission(canSetupIntegrations, 'view')}
                />
              }
            />
            <>
              <Route
                path="connect/:integration/configuration"
                element={
                  <PermissionRoute
                    element={<ConfigureIntegration />}
                    permission={hasSettingsPermission(canSetupIntegrations, 'view')}
                  />
                }
              />
              <Route path="connect/:integration/auth-callback" element={<AuthOnlyCallback />} />
              <Route path="connect/:integration" element={<AuthOnlyCallback />} />
              <Route
                path="integrate/:integration"
                element={
                  <PermissionRoute
                    element={<Integrate />}
                    permission={hasSettingsPermission(canSetupIntegrations, 'view')}
                  />
                }
              >
                <Route index element={<UserMappingsV2 />} />
                <Route path="sync-scenario" element={<SyncScenario />} />
                <Route path="field-mappings" element={<FieldMappingsV2 tableMaxHeight="100%" />} />
                <Route path="test-confirm" element={<TestRunV2 />} />
              </Route>
              <Route
                path="manage/:integration"
                element={
                  <PermissionRoute
                    element={<Manage />}
                    permission={hasSettingsPermission(canSetupIntegrations, 'view')}
                  />
                }
              >
                <Route index element={null} />
                <Route path="sync-logs" element={<SyncStatusV3 />} />
                <Route
                  path={'user-mappings'}
                  element={
                    <MappingsUpdateLayout>
                      <UserMappingsV2 maxTableHeight="100%" />
                    </MappingsUpdateLayout>
                  }
                />
                <Route
                  path={'field-mappings'}
                  element={
                    <MappingsUpdateLayout>
                      <FieldMappingsV2 tableMaxHeight="100%" />
                    </MappingsUpdateLayout>
                  }
                />
                <Route
                  path={'sync-scenario'}
                  element={
                    <MappingsUpdateLayout>
                      <SyncScenario />
                    </MappingsUpdateLayout>
                  }
                />
              </Route>
              <Route
                path="edit-mappings/:integration/:type"
                element={
                  <PermissionRoute
                    element={<EditMappings />}
                    permission={hasSettingsPermission(canSetupIntegrations, 'view')}
                  />
                }
              />
              {/* <Route
                path="integrations"
                element={
                  <PermissionRoute
                    element={<IntegrationsList />}
                    permission={hasSettingsPermission(canSetupIntegrations, 'view')}
                  />
                }
              /> */}
            </>
            <Route path="account" element={<Accounts />} />
            <Route
              path="users/manage-users"
              element={
                <PermissionRoute
                  element={<Users />}
                  permission={hasSettingsPermission(canSetupUsers, 'view')}
                />
              }
            />
            <Route
              path="users/manage-users/activity-logs"
              element={
                <PermissionRoute
                  element={<Users />}
                  permission={hasSettingsPermission(canSetupUsers, 'view')}
                />
              }
            />
            <Route
              path="users/roles-and-permissions"
              element={
                <PermissionRoute
                  element={<RolesAndPermissions />}
                  permission={hasSettingsPermission(canSetupRolesAndPermissions, 'view')}
                />
              }
            />
            <Route
              path="users/roles-and-permissions/role/create"
              element={
                <PermissionRoute
                  element={<CreateRole />}
                  permission={hasSettingsPermission(canSetupRolesAndPermissions, 'edit')}
                />
              }
            />
            <Route
              path="users/roles-and-permissions/role/update/:id"
              element={
                <PermissionRoute
                  element={<UpdateRole />}
                  permission={hasSettingsPermission(canSetupRolesAndPermissions, 'view')}
                />
              }
            />
            <Route
              path="users/roles-and-permissions/role/clone/:id"
              element={
                <PermissionRoute
                  element={<CloneRole />}
                  permission={hasSettingsPermission(canSetupRolesAndPermissions, 'edit')}
                />
              }
            />
            <Route
              path="config/intent-topics"
              element={
                <PermissionRoute
                  element={<IntentTopicsPageV2 />}
                  permission={hasSettingsPermission(canSetupIntentTopics, 'view')}
                />
              }
            />
            <Route
              path="config/buyer-roles"
              element={
                <PermissionRoute
                  element={<MillerHiemenRoles />}
                  permission={hasSettingsPermission(canSetupMillerHeimanRoles, 'view')}
                />
              }
            />
            <Route
              path="config/hpm-configuration"
              element={
                <PermissionRoute
                  element={<HPMConfiguration />}
                  permission={hasSettingsPermission(canSetupHPMConfig, 'view')}
                />
              }
            />
            <Route path="config/exclusion-list" element={<ExclusionList />} />
            <Route
              path="config/custom-fields"
              element={
                <PermissionRoute
                  element={<CustomFields />}
                  permission={hasSettingsPermission(canSetupCustomFields, 'view')}
                />
              }
            />
            <Route
              path="config/web-tracking"
              element={
                <PermissionRoute
                  element={<WebsiteTracking />}
                  permission={hasSettingsPermission(canSetupWebsiteTracking, 'view')}
                />
              }
            />
            <Route path="config/champions-tracking" element={<ChampionsTracking />} />
            {featureFlags?.apiKeys && (
              <Route path="config/api-keys" element={<ApiKeysSettings />} />
            )}
            <Route
              path="config/credentials"
              element={
                <PermissionRoute
                  element={<CredentialManagementPage />}
                  permission={hasSettingsPermission(canSetupIntegrations, 'view')}
                />
              }
            />
            {featureFlags?.automated_playbook && (
              <>
                <Route path="config/playbook" element={<RedirectComponent to="/playbook" />} />
                <Route
                  path="config/playbook/create"
                  element={<RedirectComponent to="/playbook/create" />}
                />
                <Route
                  path="config/playbook/archives"
                  element={<RedirectComponent to="/playbook/archives" />}
                />
                <Route
                  path="config/playbook/edit/:id"
                  element={<RedirectWithSingleParams to="/workflows/edit" id="id" />}
                />
                <Route
                  path="config/playbook/template-dynamic/:templateId"
                  element={
                    <RedirectWithSingleParams to="/playbook/template-dynamic" id="templateId" />
                  }
                />
              </>
            )}
            {/* {featureFlags?.automated_playbook && (
              <>
                <Route path="config/playbook" element={<PlaybookList />} />
                <Route path="config/playbook" element={<Playbook />}>
                  <Route path="create" element={<CreatePlaybook />} />
                  <Route path="edit/:id" element={<EditPlaybook />} />
                </Route>
              </>
            )} */}
            {featureFlags?.notifications && (
              <Route
                path="config/notifications"
                element={<PermissionRoute element={<NotificationsConfigs />} permission={true} />}
              />
            )}
            <Route
              path="config/icp"
              element={
                <PermissionRoute
                  element={<IcpListsV3 />}
                  permission={hasSettingsPermission(canSetupIcp, 'view')}
                />
              }
            />
            <Route
              path="config/icp"
              element={
                <PermissionRoute
                  element={<Icp />}
                  permission={hasSettingsPermission(canSetupIcp, 'edit')}
                />
              }
            >
              <Route path="create/:entity" element={<CreateIcp />} />
              <Route path="edit/:entity/:id" element={<UpdateIcp />} />
              <Route path="clone/:entity/:id" element={<CloneIcp />} />
            </Route>
            {featureFlags?.selfRecharge && (
                <Route path="credits/recharge-history" element={<RechargeHistory />} />
            )}
            <Route path="credits/credit-usage" element={<CreditUsage />} />
            {featureFlags?.userLevelCredit && (
                <Route path="credits/assign-credits" element={<PermissionRoute element={<AssignCredits />} permission={canAssignCredits} />} />
            )}
            <Route path="agent/website-agent" element={<WebsiteAgent />}>
              <Route index element={<Navigate to="general" replace />} />
              <Route path="basic-setup" element={<BasicSetup />} />
              <Route path="general" element={<General />} />
              <Route path="sprouts-signals" element={<SproutsSignals />} />
              <Route path="knowledge-base" element={<KnowledgeBase />} />
              <Route path="conversation-setup" element={<ConversationSetup />} />
            </Route>
            <Route path='config/sequence-enrollment' element={<SequenceEnrollment />} />
          </Route>
        </Route>
        <Route path="icp">
          <Route path="create" element={<RedirectComponent to="/settings/config/icp/create" />} />
          <Route
            path="update/:id"
            element={<RedirectWithSingleParams to="/settings/config/icp/edit" id="id" />}
          />
          <Route
            path="clone/:id"
            element={<RedirectWithSingleParams to="/settings/config/icp/clone" id="id" />}
          />
        </Route>

        {/** Public Routes */}
        {/** Wrap all Route under PublicRoutes element */}
        <Route path="extension/login" element={<PublicRoutes />}>
          <Route path="/extension/login" element={<ChromeExtensionRedirect />} />
        </Route>
        
        <Route path="login" element={<PublicRoutes />}>
          <Route path="/login" element={<Login />} />
        </Route>
        <Route path="signup" element={<PublicRoutes />}>
          <Route path="/signup" element={<SignUp />} />
        </Route>

        <Route path="forgot-password" element={<PublicRoutes />}>
          <Route path="/forgot-password" element={<ForgotPassword />} />
        </Route>
        <Route path="set" element={<PublicRoutes />}>
          <Route path="/set" element={<ChangePassword />} />
        </Route>
        <Route path="active" element={<PublicRoutes />}>
          <Route path="/active" element={<ActivateUser />} />
        </Route>
        <Route path="verify" element={<PublicRoutes />}>
          <Route path="/verify" element={<VerifyMagicLink />} />
        </Route>
        <Route path="login-with-password" element={<PublicRoutes />}>
          <Route path="/login-with-password" element={<LoginWithPassword />} />
        </Route>
        <Route path="magic-link" element={<PublicRoutes />}>
          <Route path="/magic-link" element={<MagicLink />} />
        </Route>
        <Route path="sso-login" element={<PublicRoutes />}>
          <Route path="/sso-login" element={<SsoLogin />} />
        </Route>
        <Route path="password-success" element={<PublicRoutes />}>
          <Route
            path="/password-success"
            element={<RedirectComponent to="/set-password-success" />}
          />
        </Route>
        <Route path="set-password-success" element={<PublicRoutes />}>
          <Route path="/set-password-success" element={<ResetPasswordSuccess />} />
        </Route>
        <Route path="forgot-password-success" element={<PublicRoutes />}>
          <Route
            path="/forgot-password-success"
            element={<RedirectComponent to="/forgot-password/success" />}
          />
        </Route>
        <Route path="forgot-password/success" element={<PublicRoutes />}>
          <Route path="/forgot-password/success" element={<ForgotPasswordSuccess />} />
        </Route>
        <Route path="/linkedin-connect/*" element={<LinkedInConnectRedirect />} />
        <Route path="/linkedin-custom-connect/*" element={<LinkedInConnectRedirect />} />
        <Route path="/chatbot" element={<ChatbotSiri />} />
        <Route path="/oauth-completed" element={<OAuthCompleted />} />
        <Route path="test-integration" element={<TestIntegration />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
};

export default MainRoutes;
